import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import {
  addDays,
  addMonths,
  getDaysInMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import type { GanttZoomLevel, StepDirection } from './types';

/**
 * The planner's chosen span. Month view counts in months rather than days
 * (SC-6253): months differ in length, so "2 months" has to be resolved against
 * the cursor each render instead of frozen into a day count that goes stale as
 * soon as the view is paged.
 */
export interface GanttRange {
  unit: 'days' | 'months';
  count: number;
}

interface GanttViewControlsProps {
  zoomLevel: GanttZoomLevel;
  onZoomChange: (zoom: GanttZoomLevel) => void;
  /** Span chosen by the planner, or null to use the zoom's own. */
  range: GanttRange | null;
  onRangeChange: (range: GanttRange) => void;
}

const ZOOM_OPTIONS: { value: GanttZoomLevel; label: string }[] = [
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

/** Matches the Grid and Map range selectors so the toolbar reads consistently. */
export const GANTT_RANGE_OPTIONS = [1, 2, 3, 4, 5, 6, 7];

/** Month view ranges in whole months — a 1–7 day span is meaningless there. */
export const GANTT_MONTH_OPTIONS = [1, 2, 3];

function pluralise(count: number, unit: GanttRange['unit']): string {
  const noun = unit === 'months' ? 'month' : 'day';
  return `${count} ${count === 1 ? noun : `${noun}s`}`;
}

export function GanttViewControls({
  zoomLevel,
  onZoomChange,
  range,
  onRangeChange,
}: GanttViewControlsProps) {
  const isMonthZoom = zoomLevel === 'month';
  const unit: GanttRange['unit'] = isMonthZoom ? 'months' : 'days';
  const options = isMonthZoom ? GANTT_MONTH_OPTIONS : GANTT_RANGE_OPTIONS;

  // The zoom sets the span; the selector then overrides it (SC-6252/SC-6253).
  // Showing the zoom's own span while no override is set keeps the two controls
  // agreeing, instead of the dropdown claiming a range the chart isn't showing.
  // A range carried over from another zoom is ignored — its unit won't match.
  const selectedCount =
    range?.unit === unit ? range.count : isMonthZoom ? 1 : ganttDaysToShow(zoomLevel, new Date());

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center border border-border rounded-md overflow-hidden">
        {ZOOM_OPTIONS.map((option) => (
          <Button
            key={option.value}
            variant="ghost"
            size="sm"
            onClick={() => onZoomChange(option.value)}
            className={`rounded-none h-8 px-3 ${
              zoomLevel === option.value
                ? 'bg-[#E84A2B] text-white hover:bg-[#d43d20] hover:text-white'
                : ''
            }`}
          >
            {option.label}
          </Button>
        ))}
      </div>

      <Select
        value={options.includes(selectedCount) ? selectedCount.toString() : ''}
        onValueChange={(value) => onRangeChange({ unit, count: Number.parseInt(value, 10) })}
      >
        <SelectTrigger
          aria-label="Date range"
          className="w-[140px] bg-background border-border text-foreground h-8"
        >
          {/* A span outside the offered options still needs a readable label
              rather than an empty trigger. */}
          <SelectValue placeholder={pluralise(selectedCount, unit)} />
        </SelectTrigger>
        <SelectContent className="bg-popover border-border">
          {options.map((count) => (
            <SelectItem key={count} value={count.toString()}>
              {pluralise(count, unit)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Derive the visible span (in days) from the current zoom level.
 * Month uses the actual number of days in `cursor`'s month so February
 * isn't padded out to 31.
 *
 * `range` is the planner's own choice from the range selector (SC-6252/SC-6253).
 * The zoom level sets the default span and the range replaces it, so switching
 * zoom still gives that zoom's natural window until it is changed.
 *
 * A range in months is resolved against `cursor` rather than assuming 30-day
 * months, so "2 months" from 1 Feb is 59 days and from 1 Jul is 62.
 */
export function ganttDaysToShow(
  zoomLevel: GanttZoomLevel,
  cursor: Date,
  range?: GanttRange | null,
): number {
  if (range && range.count > 0) {
    if (range.unit === 'days') return range.count;

    let days = 0;
    let month = startOfMonth(cursor);
    for (let i = 0; i < range.count; i++) {
      days += getDaysInMonth(month);
      month = addMonths(month, 1);
    }
    return days;
  }

  switch (zoomLevel) {
    case 'hour':
    case 'day':
      return 1;
    case 'week':
      return 7;
    case 'month':
      return getDaysInMonth(cursor);
  }
}

/**
 * Snap the chart's left edge based on zoom level so Week starts on Monday
 * and Month starts on the 1st.
 */
export function ganttRangeStart(zoomLevel: GanttZoomLevel, cursor: Date): Date {
  switch (zoomLevel) {
    case 'week':
      return startOfWeek(cursor, { weekStartsOn: 1 });
    case 'month':
      return startOfMonth(cursor);
    case 'hour':
    case 'day':
    default:
      return startOfDay(cursor);
  }
}

/**
 * Move the navigation cursor by one "unit" of the active zoom.
 *  - hour/day: ±1 day
 *  - week: ±7 days (rangeStart will re-snap to Monday)
 *  - month: ±1 calendar month
 *
 * When the planner has chosen their own range (SC-6252/SC-6253), paging follows
 * that span instead — stepping a week at a time through a 3-day window would
 * skip four days of work with every click. A range in months steps by whole
 * months so the view stays aligned to the 1st.
 */
export function ganttStepDate(
  zoomLevel: GanttZoomLevel,
  cursor: Date,
  direction: StepDirection,
  range?: GanttRange | null,
): Date {
  if (range && range.count > 0) {
    return range.unit === 'months'
      ? addMonths(cursor, direction * range.count)
      : addDays(cursor, direction * range.count);
  }

  switch (zoomLevel) {
    case 'hour':
    case 'day':
      return addDays(cursor, direction);
    case 'week':
      return addDays(cursor, direction * 7);
    case 'month':
      return addMonths(cursor, direction);
  }
}
